import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthCtx = createContext(null);

export const ADMIN_HOME = "/dashboard";
export const CUSTOMER_HOME = "/customer/dashboard";

export function roleHome(role) {
  return role === "customer" ? CUSTOMER_HOME : ADMIN_HOME;
}

export function AuthProvider({ children }) {
  // null = checking, false = anon, object = authenticated
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data?.token) localStorage.setItem("1ai_token", data.token);
      const nextUser = {
        id: data.id,
        email: data.email,
        name: data.name,
        role: data.role,
        contact_name: data.contact_name,
        assigned_customer_ids: data.assigned_customer_ids || [],
        last_login_at: data.last_login_at,
      };
      setUser(nextUser);
      return nextUser;
    } catch (e) {
      setError(formatApiError(e));
      return null;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    localStorage.removeItem("1ai_token");
    setUser(false);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, error, login, logout, refresh, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
